
import { Component, EventEmitter, Input, Output, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { selectUserInfo } from '../../../../core/store/auth/auth.selectors';
import { ProceduresService } from '../../../../core/services/procedures.service';
import { filter, take } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';

interface DoctorWithPercent {
    doctor: any;
    percent: number;
    honoraryId: number | null;
    editing: boolean;
    tempPercent: number; // For binding input
    loading: boolean;
}

@Component({
    selector: 'app-medical-percentages-modal',
    standalone: true,
    imports: [
        CommonModule, 
        FormsModule, 
        TranslateModule,
        DialogModule,
        InputNumberModule,
        ButtonModule
    ],
    templateUrl: './medical-percentages-modal.html',
    styles: [`
        :host ::ng-deep {
            .p-dialog {
                border-radius: 24px !important;
                overflow: hidden !important;
                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
                border: none !important;
            }
            .p-dialog .p-dialog-header {
                padding: 2rem 2rem 1rem 2rem !important;
                background: white !important;
            }
            .p-dialog .p-dialog-content {
                padding: 1rem 2rem 2rem 2rem !important;
                background: white !important;
            }
            .p-dialog .p-dialog-header-icons {
                gap: 0.5rem !important;
            }
            .p-dialog .p-dialog-header-close-icon {
                color: #94a3b8 !important;
                transition: color 0.2s !important;
            }
            .p-dialog .p-dialog-header-close-icon:hover {
                color: #475569 !important;
            }

            .percent-input .p-inputnumber-input {
                width: 70px !important;
                height: 36px !important;
                text-align: center !important;
                font-size: 13px !important;
                font-weight: 700 !important;
                border-radius: 10px !important;
                background: #f8fafc !important;
                border: 1px solid #e2e8f0 !important;
            }
            .percent-input .p-inputnumber-input:focus {
                border-color: var(--primary-color) !important;
                box-shadow: 0 0 0 4px rgba(var(--primary-color-rgb), 0.1) !important;
                outline: none !important;
            }
        }
    `]
})
export class MedicalPercentagesModalComponent implements OnInit {
    @Input() isOpen = false;
    @Input() procedureId: number | null = null;
    @Input() procedureName = '';
    @Output() close = new EventEmitter<void>();

    branchId: number | null = null;
    loading = false;
    doctorsList: DoctorWithPercent[] = [];

    constructor(
        private proceduresService: ProceduresService,
        private store: Store,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit() {
        this.store.select(selectUserInfo).pipe(
            filter(info => !!info),
            take(1)
        ).subscribe((userInfo: any) => {
            this.branchId = userInfo.branches_id || userInfo.branchId;
        });
    }

    // Hook into when isOpen changes or procedureId changes to load data
    // But standard pattern is call load when opening.
    // We'll implement ngOnChanges if needed or assume parent sets inputs then sets isOpen.

    ngOnChanges() {
        if (this.isOpen && this.procedureId && this.branchId) {
            this.loadData();
        }
    }

    async loadData() {
        if (!this.branchId || !this.procedureId) return;
        this.loading = true;
        try {
            const doctorsRes = await this.proceduresService.getDoctors(this.branchId);
            const honorariesRes = await this.proceduresService.getProcedureHonoraries(this.procedureId);

            const doctors = doctorsRes.data || [];
            const honoraries = honorariesRes.data || [];

            this.doctorsList = doctors.map((doc: any) => {
                const honorary = honoraries.find((h: any) => h.doctor_id === doc.id);
                const percent = honorary ? (honorary.percent || 0) : 0;
                return {
                    doctor: doc,
                    percent: percent,
                    honoraryId: honorary ? honorary.id : null,
                    editing: false,
                    tempPercent: percent,
                    loading: false
                };
            });

        } catch (error) {
            console.error('Error loading medical percentages:', error);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    onClose() {
        this.close.emit();
        this.doctorsList = [];
    }

    startEdit(item: DoctorWithPercent) {
        item.editing = true;
        item.tempPercent = item.percent;
    }

    async saveEdit(item: DoctorWithPercent) {
        if (!this.branchId || !this.procedureId) return;

        item.loading = true;
        try {
            const payload = {
                id: item.honoraryId,
                doctor_id: item.doctor.id,
                procedure_id: this.procedureId,
                branches_id: this.branchId,
                percent: item.tempPercent
            };

            const { data, error } = await this.proceduresService.upsertProcedureHonorary(payload);

            if (error) throw error;

            if (data) {
                item.percent = data.percent;
                item.honoraryId = data.id;
                item.editing = false;
            }

        } catch (error) {
            console.error('Error saving percentage:', error);
        } finally {
            item.loading = false;
            this.cdr.detectChanges();
        }
    }
}
